import {compact} from 'lodash';

import {type MediaBrowserPanelConfig} from '../common';
import {PERFORMANCE_THRESHOLDS} from '../layout/constants';
import {getCombinedHistorySpecs, getPanelMediaKeys} from './queryConfigHelpers';
import {
  getPaginatedLoadTier,
  type TrackingConfigContext,
} from './trackingConfigHelper';
import {type MediaHistorySpec, type MediaPanelModeConfig} from './types';

function getRequiredRunNames(config: MediaBrowserPanelConfig): string[] {
  const compareSettings = config.compareSettings;
  if (compareSettings?.tileControls?.run === 'individual') {
    return Array.from(
      new Set(compact(compareSettings.columns?.map(column => column.run) ?? []))
    );
  }

  if (
    compareSettings?.tileControls?.run === 'linked' &&
    compareSettings.runName != null
  ) {
    return [compareSettings.runName];
  }

  return [];
}

function getRunFilterMediaKeys(config: MediaBrowserPanelConfig): string[] {
  if (config.compareSettings?.tileControls?.mediaKey === 'individual') {
    const mediaKeys = config.mediaKeys ?? [];
    const columnMediaKeys =
      config.compareSettings.columns?.map(column => column.mediaKey) ?? [];

    return Array.from(new Set(compact([...mediaKeys, ...columnMediaKeys])));
  }

  return getPanelMediaKeys(config);
}

function shouldFilterRunsInQuery(config: MediaBrowserPanelConfig): boolean {
  return config.compareSettings?.tileControls?.run === 'individual';
}

function getHistorySpecs(
  config: MediaBrowserPanelConfig,
  mediaSliderKey: string,
  samples: number
): MediaHistorySpec[] | undefined {
  if (config.compareSettings?.tileControls?.mediaKey === 'individual') {
    const runFilterMediaKeys = getRunFilterMediaKeys(config);
    if (runFilterMediaKeys.length === 0) {
      return undefined;
    }

    return runFilterMediaKeys.map(mediaKey => ({
      keys: [...new Set(['_step', mediaSliderKey, mediaKey])],
      samples,
    }));
  }

  return getCombinedHistorySpecs(config, mediaSliderKey, samples);
}

function getComparePageCap({
  columnCount,
  compareRowCount,
  fanOutVariable,
}: Pick<
  TrackingConfigContext,
  'columnCount' | 'compareRowCount' | 'fanOutVariable'
>): number | null {
  if (!fanOutVariable) {
    return null;
  }

  const cols = columnCount > 0 ? columnCount : 1;
  const maxRows = PERFORMANCE_THRESHOLDS.MAX_COMPARE_ROWS_WITHOUT_PAGINATION;
  if (compareRowCount > maxRows) {
    return maxRows * cols;
  }
  return null;
}

export const compareModeConfig: MediaPanelModeConfig = {
  imageFitAndResolutionConfigFields: ['alwaysOriginal', 'default'],
  // Columns can reference runs that do not log the key; keep them and show per-tile errors.
  filterToRunsWithMediaKey: false,
  // Compare lays media out in its own column/row structure; no row-height management.
  constrainsContentRowHeight: false,
  // Compare lays tiles out in its own column/row structure, never content-sized.
  shouldSizeTilesByContent: () => false,
  getRequiredRunNames,
  getRunFilterMediaKeys,
  shouldFilterRunsInQuery,
  getHistorySpecs,
  getPageCap: getComparePageCap,
  getLoadTier: ({activeTileCount, layoutTileCount, pageCap}) =>
    getPaginatedLoadTier(activeTileCount, layoutTileCount, pageCap),
  getPaginationTileTotal: ({compareRowCount, columnCount}) =>
    compareRowCount * columnCount,
};
